import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../utils/apiConfig';

/**
 * Page de vérification d'email (lors du changement d'adresse)
 * Accessible via le lien envoyé par email avec un token
 */
const VerifyEmail = ({ token, onBackToLogin, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [currentEmail, setCurrentEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Vérifier le token au chargement
  useEffect(() => {
    const verifyToken = async () => {
      try {
        const response = await fetch(`${API_ENDPOINTS.AUTH}/verify-email-token/${token}`);
        const data = await response.json();

        if (data.success) {
          setTokenValid(true);
          setCurrentEmail(data.data.currentEmail);
          setNewEmail(data.data.newEmail);
          setUserName(data.data.firstName || data.data.currentEmail.split('@')[0]);
        } else {
          setError(data.error || 'Token invalide ou expiré');
        }
      } catch (err) {
        setError('Erreur de connexion au serveur');
      } finally {
        setVerifying(false);
      }
    };

    if (token) {
      verifyToken();
    } else {
      setVerifying(false);
      setError('Token manquant');
    }
  }, [token]);

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_ENDPOINTS.AUTH}/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        if (onSuccess) {
          setTimeout(() => onSuccess(), 3000);
        }
      } else {
        setError(data.error || 'Erreur lors de la vérification');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px'
    },
    card: {
      background: 'white',
      borderRadius: '12px',
      padding: '40px',
      width: '100%',
      maxWidth: '450px',
      boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
    },
    logo: {
      textAlign: 'center',
      marginBottom: '30px'
    },
    logoImg: {
      height: '50px'
    },
    title: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#333',
      marginBottom: '10px',
      textAlign: 'center'
    },
    subtitle: {
      fontSize: '14px',
      color: '#666',
      marginBottom: '30px',
      textAlign: 'center'
    },
    emailBox: {
      background: '#f8f9fa',
      padding: '20px',
      borderRadius: '8px',
      marginBottom: '20px'
    },
    emailRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 0',
      borderBottom: '1px solid #eee'
    },
    emailRowLast: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 0'
    },
    emailLabel: {
      fontSize: '13px',
      color: '#666'
    },
    emailValue: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#333'
    },
    arrow: {
      textAlign: 'center',
      padding: '10px',
      fontSize: '24px',
      color: '#667eea'
    },
    button: {
      width: '100%',
      padding: '14px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s'
    },
    buttonDisabled: {
      opacity: 0.7,
      cursor: 'not-allowed'
    },
    cancelButton: {
      width: '100%',
      padding: '12px',
      background: 'transparent',
      color: '#666',
      border: '1px solid #ddd',
      borderRadius: '8px',
      fontSize: '14px',
      cursor: 'pointer',
      marginTop: '10px'
    },
    error: {
      background: '#fee',
      color: '#c00',
      padding: '12px',
      borderRadius: '8px',
      fontSize: '14px',
      textAlign: 'center',
      marginBottom: '20px'
    },
    success: {
      background: '#e8f5e9',
      color: '#2e7d32',
      padding: '20px',
      borderRadius: '8px',
      textAlign: 'center'
    },
    successIcon: {
      fontSize: '48px',
      marginBottom: '15px'
    },
    successTitle: {
      fontSize: '18px',
      fontWeight: '600',
      marginBottom: '10px'
    },
    successText: {
      fontSize: '14px',
      lineHeight: '1.5'
    },
    backLink: {
      textAlign: 'center',
      marginTop: '20px'
    },
    link: {
      color: '#667eea',
      textDecoration: 'none',
      fontSize: '14px',
      cursor: 'pointer'
    },
    loading: {
      textAlign: 'center',
      padding: '40px',
      color: '#666'
    },
    loadingIcon: {
      fontSize: '32px',
      marginBottom: '15px'
    },
    warning: {
      background: '#fff3e0',
      color: '#e65100',
      padding: '12px',
      borderRadius: '8px',
      fontSize: '13px',
      marginBottom: '20px',
      textAlign: 'center'
    }
  };

  // Affichage pendant la vérification
  if (verifying) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.logo}>
            <img src="/logo_connect.png" alt="R-Connect" style={styles.logoImg} />
          </div>
          <div style={styles.loading}>
            <div style={styles.loadingIcon}>⏳</div>
            <div>Vérification du lien...</div>
          </div>
        </div>
      </div>
    );
  }

  // Token invalide
  if (!tokenValid) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.logo}>
            <img src="/logo_connect.png" alt="R-Connect" style={styles.logoImg} />
          </div>
          <div style={{ ...styles.error, padding: '30px', marginBottom: 0 }}>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>❌</div>
            <div style={{ fontWeight: '600', marginBottom: '10px' }}>Lien invalide ou expiré</div>
            <div style={{ fontWeight: 'normal' }}>
              Ce lien de vérification n'est plus valide. Les liens expirent après 24 heures.
              <br /><br />
              Si vous souhaitez toujours changer d'adresse email, veuillez refaire une demande depuis votre profil.
            </div>
          </div>
          <div style={styles.backLink}>
            <span style={styles.link} onClick={onBackToLogin}>
              ← Retour à la connexion
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Succès
  if (success) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.logo}>
            <img src="/logo_connect.png" alt="R-Connect" style={styles.logoImg} />
          </div>
          <div style={styles.success}>
            <div style={styles.successIcon}>✅</div>
            <div style={styles.successTitle}>Adresse email mise à jour !</div>
            <div style={styles.successText}>
              Votre nouvelle adresse email est maintenant active.
              <br /><br />
              <strong>{newEmail}</strong>
              <br /><br />
              Vous pouvez désormais vous connecter avec cette adresse.
            </div>
          </div>
          <div style={styles.backLink}>
            <span style={styles.link} onClick={onBackToLogin}>
              Se connecter
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Formulaire de confirmation
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logo}>
          <img src="/logo_connect.png" alt="R-Connect" style={styles.logoImg} />
        </div>

        <h1 style={styles.title}>Confirmer le changement d'email</h1>
        <p style={styles.subtitle}>
          Bonjour {userName}, confirmez-vous ce changement d'adresse email ?
        </p>

        {error && <div style={styles.error}>{error}</div>}

        <div style={styles.emailBox}>
          <div style={styles.emailRow}>
            <span style={styles.emailLabel}>Adresse actuelle</span>
            <span style={styles.emailValue}>{currentEmail}</span>
          </div>
          <div style={styles.arrow}>↓</div>
          <div style={styles.emailRowLast}>
            <span style={styles.emailLabel}>Nouvelle adresse</span>
            <span style={{ ...styles.emailValue, color: '#667eea' }}>{newEmail}</span>
          </div>
        </div>

        <div style={styles.warning}>
          Attention : après confirmation, vous devrez utiliser la nouvelle adresse pour vous connecter.
        </div>

        <button
          onClick={handleConfirm}
          disabled={loading}
          style={{
            ...styles.button,
            ...(loading ? styles.buttonDisabled : {})
          }}
        >
          {loading ? 'Confirmation en cours...' : 'Confirmer le changement'}
        </button>

        <button
          onClick={onBackToLogin}
          style={styles.cancelButton}
        >
          Annuler
        </button>

        <div style={styles.backLink}>
          <span style={styles.link} onClick={onBackToLogin}>
            ← Retour à la connexion
          </span>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
